import { IRequest } from '../../httpClient/serverApi/types/IRequest';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { ErrorReason } from '../../sessionManagement/SessionManagementService';

export const defaultPollInterval = 1000; // 1 sec

export class RequestPoller<TParams, TResult> {
  /* the request to trigger periodically */
  private request: IRequest<TParams, TResult>;

  /* the function to determine if polling should stop whenever result comes back */
  private isComplete: (result: TResult) => boolean;

  /* the function to call when result comes back and isComplete is true */
  private successCallback: (result: TResult) => void;

  /* the function to call when result comes back and isComplete is false */
  private notifyCallback: (result: TResult) => void;

  /* the function to call when request is rejected or polling is canceled */
  private failureCallback: (reason: ErrorReason) => void;

  /* the delay after each poll, in milliseconds */
  private pollInterval: number;

  private intervalHandle: NodeJS.Timeout;

  constructor(
    request: IRequest<TParams, TResult>,
    isComplete: (result: TResult) => boolean,
    successCallback?: (result: TResult) => void,
    failureCallback?: (reason: ErrorReason) => void,
    notifyCallback?: (result: TResult) => void,
    pollInterval: number = defaultPollInterval,
  ) {
    this.request = request;
    this.isComplete = isComplete;
    this.successCallback = successCallback;
    this.failureCallback = failureCallback;
    this.notifyCallback = notifyCallback;
    this.pollInterval = pollInterval;
  }

  public startPolling(): void {
    if (this.intervalHandle) {
      return; // polling has already started
    }

    const onRequestFulfifilled = (result: TResult) => {
      if (this.isComplete(result)) {
        clearInterval(this.intervalHandle);
        this.successCallback && this.successCallback(result);
      } else {
        this.notifyCallback && this.notifyCallback(result);
      }
    };

    const onRequestRejected = (reason: ErrorReason) => {
      clearInterval(this.intervalHandle);
      this.failureCallback && this.failureCallback(reason);
    };

    const poll = () => WebHttpClient.sendRequest(this.request)
      .then(onRequestFulfifilled, onRequestRejected);
    this.intervalHandle = setInterval(poll, this.pollInterval);
  }

  public cancelPolling(): void {
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
      this.failureCallback && this.failureCallback(ErrorReason.Canceled);
    }
  }
}
